import { useState, useEffect } from 'react';
import { api } from '../services/api';
import type { RoomResponse } from '../types/auth';
import { eventItemMatchesDirections } from '../constants/directions';

const STALE_MS = 30_000;

interface CacheEntry {
  data: RoomResponse[];
  fetchedAt: number;
}

export interface UpcomingRoomsFilters {
  direction?: string | null; // applied client-side via filterRoomsByDirection
  date?: string | null; // applied client-side — public-rooms endpoint has no date param
}

const cache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<RoomResponse[]>>();

const BACKEND_KEY = 'all'; // The hook fetches the full public list; filtering happens client-side.

/**
 * Drop rooms whose status is 'ended' — the public-rooms endpoint can
 * return mixed statuses (live, scheduled, ended) and the upcoming tab
 * should never show recordings. Case-insensitive on the status string.
 */
export function filterOutEnded(rooms: RoomResponse[]): RoomResponse[] {
  return rooms.filter((r) => String(r.status).toLowerCase() !== 'ended');
}

/**
 * Pure: a room's practice window has ended. Returns true when:
 *   • `endedAtUtc` is set (BE flipped Ended), OR
 *   • status is literally 'Ended', OR
 *   • `scheduledAt + duration + 15-min slack` is in the past — this catches
 *     the «Тратак at 00:07 still shows on Скоро at 12:46» case where BE
 *     never formally closed a self-guided / cron-started room. We add 15
 *     minutes of slack so a room running slightly late doesn't drop off
 *     the live list mid-broadcast.
 *
 * The same predicate drives Скоро's drop-list (via `filterOutPast`) and
 * Архив's supplement (in useArchiveRooms) so the two tabs partition the
 * room set identically. Pure helper — exported for unit testing.
 */
export function isRoomPast(r: RoomResponse, now: number = Date.now()): boolean {
  if (r.endedAtUtc) return true;
  if (String(r.status).toLowerCase() === 'ended') return true;

  const scheduled = r.scheduledAtUtc ?? r.startedAtUtc;
  if (!scheduled) return false;
  const startMs = new Date(scheduled).getTime();
  if (!Number.isFinite(startMs)) return false;

  const duration =
    r.durationMinutes ?? r.videoPracticeDurationMinutes ?? 60;
  const windowEndMs = startMs + (duration + 15) * 60_000;
  return windowEndMs <= now;
}

/** Inverse of isRoomPast — keeps rooms whose practice is still in flight
 *  or scheduled in the future. Used by useUpcomingRooms's pipeline. */
export function filterOutPast(
  rooms: RoomResponse[],
  now: number = Date.now(),
): RoomResponse[] {
  return rooms.filter((r) => !isRoomPast(r, now));
}

/**
 * Filter rooms by a direction tag (e.g. 'Утренняя практика', 'Медитация').
 * Storage on the BE side uses the **Russian display names** as IDs (the
 * single source of truth lives in `constants/directions.EVENT_DIRECTIONS`),
 * stored either as a `categories[]` array (preferred) or the legacy
 * `category` CSV field. This helper delegates to the shared
 * `eventItemMatchesDirections` reader so the two storage shapes are
 * handled uniformly — same logic the offline calendar / lives filters use.
 */
export function filterRoomsByDirection(
  rooms: RoomResponse[],
  direction: string | null,
): RoomResponse[] {
  if (!direction) return rooms;
  return rooms.filter((r) => eventItemMatchesDirections(r, [direction]));
}

/**
 * Filter rooms by a single ISO local-day (yyyy-mm-dd). Compares against
 * scheduledAtUtc (preferred) or startedAtUtc, projected into local time.
 * Pure helper — exported for unit testing without mounting the hook.
 */
export function filterUpcomingByDate(
  rooms: RoomResponse[],
  isoDate: string | null,
): RoomResponse[] {
  if (!isoDate) return rooms;
  return rooms.filter((r) => {
    const t = r.scheduledAtUtc ?? r.startedAtUtc;
    if (!t) return false;
    const d = new Date(t);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}` === isoDate;
  });
}

/**
 * Module-level cached fetcher. Caches the full RAW public-rooms response
 * — including Ended-status rooms. Both Скоро (which strips ended via
 * `filterOutEnded` + past via `filterOutPast` in the hook) and Архив
 * (which surfaces past-by-duration rooms via `isRoomPast` from the
 * supplement) read from this single cache. Filtering at-cache used to
 * strip `status==='Ended'` before persisting; that hid those rooms from
 * the Архив supplement too — the «owner created a room, ended it, and
 * Архив stayed empty» bug. Pure fetch now, derive both views downstream.
 */
export async function fetchUpcomingRoomsCached(): Promise<RoomResponse[]> {
  const now = Date.now();
  const hit = cache.get(BACKEND_KEY);
  if (hit && now - hit.fetchedAt < STALE_MS) return hit.data;

  const pending = inflight.get(BACKEND_KEY);
  if (pending) return pending;

  const promise = api
    .getPublicRooms({ limit: 50 })
    .then((res) => {
      cache.set(BACKEND_KEY, { data: res.items, fetchedAt: Date.now() });
      inflight.delete(BACKEND_KEY);
      return res.items;
    })
    .catch((err) => {
      inflight.delete(BACKEND_KEY);
      throw err;
    });
  inflight.set(BACKEND_KEY, promise);
  return promise;
}

/** Test-only — clear the module cache between tests. */
export function _resetUpcomingRoomsCacheForTest(): void {
  cache.clear();
  inflight.clear();
}

export function useUpcomingRooms(filters: UpcomingRoomsFilters): {
  rooms: RoomResponse[];
  isLoading: boolean;
  error: unknown;
} {
  const direction = filters.direction ?? null;
  const date = filters.date ?? null;

  const applyFilters = (data: RoomResponse[]): RoomResponse[] =>
    filterUpcomingByDate(
      filterOutPast(filterOutEnded(filterRoomsByDirection(data, direction))),
      date,
    );

  const initialHit = cache.get(BACKEND_KEY);
  const initialFresh = !!(initialHit && Date.now() - initialHit.fetchedAt < STALE_MS);

  const [rooms, setRooms] = useState<RoomResponse[]>(() =>
    initialFresh ? applyFilters(initialHit!.data) : [],
  );
  const [isLoading, setIsLoading] = useState(!initialFresh);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let mounted = true;

    const hit = cache.get(BACKEND_KEY);
    if (hit && Date.now() - hit.fetchedAt < STALE_MS) {
      setRooms(applyFilters(hit.data));
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    fetchUpcomingRoomsCached()
      .then((data) => {
        if (!mounted) return;
        setRooms(applyFilters(data));
        setError(null);
      })
      .catch((err) => {
        if (!mounted) return;
        setError(err);
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });

    return () => {
      mounted = false;
    };
    // applyFilters depends on direction + date; both are explicit deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [direction, date]);

  return { rooms, isLoading, error };
}
